/**
 * Manual hooks for the Leads (deal attribution) API.
 * These follow the same pattern as the orval-generated hooks but are
 * hand-authored because the leads endpoints were added after initial codegen.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

// ── Types ──────────────────────────────────────────────────────────────────

export interface LeadInput {
  listingId: number;
  customerName: string;
  customerWhatsapp: string;
  customerEmail?: string | null;
  consentGiven: true;
}

export interface LeadResponse {
  referenceCode: string;
  listingTitle: string;
  merchantContact: {
    whatsapp: string | null;
    phone: string | null;
    email: string | null;
  };
}

export interface AdminLead {
  id: number;
  referenceCode: string;
  listingId: number;
  listingTitle: string;
  merchantId: number;
  merchantName: string;
  customerName: string;
  customerWhatsapp: string;
  customerEmail: string | null;
  status: "new" | "contacted" | "qualified" | "converted" | "lost";
  commercialModel: string | null;
  feeValue: number | null;
  commissionDue: number | null;
  paymentStatus: "not_due" | "due" | "paid";
  adminNotes: string | null;
  notificationSent: boolean;
  createdAt: string;
}

export interface LeadStatusUpdate {
  status?: "new" | "contacted" | "qualified" | "converted" | "lost";
  commercialModel?: string | null;
  feeValue?: number | null;
  commissionDue?: number | null;
  paymentStatus?: "not_due" | "due" | "paid";
  adminNotes?: string | null;
}

// ── Mutations ──────────────────────────────────────────────────────────────

export const submitLead = (body: LeadInput, options?: RequestInit) =>
  customFetch<LeadResponse>("/api/leads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...options,
  });

export const useSubmitLead = (options?: Parameters<typeof useMutation>[0]) =>
  useMutation({
    mutationKey: ["submitLead"],
    mutationFn: ({ data }: { data: LeadInput }) => submitLead(data),
    ...(options as object),
  });

export const updateAdminLead = (id: number, body: LeadStatusUpdate, options?: RequestInit) =>
  customFetch<{ id: number; referenceCode: string; status: string; paymentStatus: string }>(
    `/api/admin/leads/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      ...options,
    },
  );

export const useAdminUpdateLead = (options?: Parameters<typeof useMutation>[0]) =>
  useMutation({
    mutationKey: ["adminUpdateLead"],
    mutationFn: ({ id, data }: { id: number; data: LeadStatusUpdate }) => updateAdminLead(id, data),
    ...(options as object),
  });

// ── Queries ────────────────────────────────────────────────────────────────

export const adminListLeads = (options?: RequestInit) =>
  customFetch<AdminLead[]>("/api/admin/leads", options);

export const getAdminListLeadsQueryKey = () => ["/api/admin/leads"] as const;

export const useAdminListLeads = (queryOptions?: object) =>
  useQuery({
    queryKey: getAdminListLeadsQueryKey(),
    queryFn: () => adminListLeads(),
    ...(queryOptions as object),
  });
